import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useMonthStore } from '@/lib/monthStore';
import { usePeriodStore, monthIdsQuery } from '@/lib/periodStore';
import { GlassCard } from '@/components/ui/GlassCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkuStatusBadge } from '@/components/ui/StatusBadge';
import { formatCurrency, formatNumber, formatPercent } from '@/lib/format';
import { cn } from '@/lib/cn';
import type {
  ReorderSuggestion,
  InventorySummary,
  InventoryResponse,
  ExcludedMonth,
  SafetyStock,
} from '@/types';

export default function Inventory() {
  const selectedMonthId = useMonthStore((s) => s.selectedMonthId);
  const monthIds = usePeriodStore((s) => s.monthIds);
  const isMultiMonth = monthIds.length > 1;

  const [suggestions, setSuggestions] = useState<ReorderSuggestion[]>([]);
  const [summary, setSummary] = useState<InventorySummary | null>(null);
  const [invAgg, setInvAgg] = useState<InventoryResponse | null>(null);
  const [excludedMonths, setExcludedMonths] = useState<ExcludedMonth[]>([]);
  const [safetyStock, setSafetyStock] = useState<SafetyStock[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (monthIds.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setExcludedMonths([]);

    if (isMultiMonth) {
      const q = monthIdsQuery(monthIds);
      api
        .get<InventoryResponse>(`/aggregate/inventory?monthIds=${q}`)
        .then((res) => {
          setInvAgg(res);
          setExcludedMonths(res.excluded);
          setSummary({
            totalWasteUnits: res.inventory.totalWasteUnits,
            totalWasteCost: res.inventory.totalWasteCost,
            totalWastePercent: res.inventory.totalWastePercent,
            inventoryTurnover: res.inventory.avgInventoryTurnover,
            shrinkageRate: res.inventory.shrinkageRate,
            stockoutDays: res.inventory.stockoutIncidents,
            criticalDays: res.inventory.criticalIncidents,
            excessDays: res.inventory.excessDays,
          });
          setSuggestions([]);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      const id = selectedMonthId || monthIds[0];
      if (!id) {
        setLoading(false);
        return;
      }
      Promise.all([
        api.get<ReorderSuggestion[]>(`/inventory/month/${id}/reorder-suggestions`),
        api.get<InventorySummary>(`/inventory/month/${id}/summary`),
        api.get<SafetyStock[]>(`/inventory/month/${id}/safety-stock`).catch(() => []),
      ])
        .then(([s, sum, ss]) => {
          setSuggestions(s);
          setSummary(sum);
          setInvAgg(null);
          setSafetyStock(ss);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [selectedMonthId, monthIds, isMultiMonth]);

  if (monthIds.length === 0) {
    return (
      <>
        <PageHeader tag="// MÓDULO :: 03" title="Inventario" subtitle="Stock, merma y sugerencias de compra" />
        <EmptyState
          icon="inventory_2"
          title="Sin mes seleccionado"
          description="Carga un archivo Excel para ver el inventario."
        />
      </>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="material-symbols-outlined text-5xl text-primary animate-spin">
          progress_activity
        </span>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        tag="// MÓDULO :: 03 / INVENTARIO"
        title="Inventario & Producción"
        subtitle={
          isMultiMonth
            ? `Vista multi-mes · ${monthIds.length} meses · merma, rotación y stockouts agregados`
            : 'Sugerencias de compra y resumen del mes'
        }
      />

      {excludedMonths.length > 0 && (
        <div className="mb-6 bg-surface-container border-2 border-warning shadow-[3px_3px_0_0_#000000] p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-warning mt-0.5">warning</span>
          <div className="flex-1">
            <p className="font-bold text-text-bright font-mono uppercase tracking-widest text-sm">
              ⚠ {excludedMonths.length} mes(es) excluido(s) por baja calidad
            </p>
            <ul className="text-text-muted mt-2 text-xs space-y-1 list-disc list-inside font-mono">
              {excludedMonths.map((m) => (
                <li key={m.monthId}>
                  {m.fileName} — {m.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {summary && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-6">
          <SummaryBox
            label="Merma Total"
            value={formatNumber(summary.totalWasteUnits)}
            unit="unidades"
            icon="delete"
            variant="warning"
            sub={formatPercent(summary.totalWastePercent)}
          />
          <SummaryBox
            label="Costo Merma"
            value={formatCurrency(summary.totalWasteCost)}
            unit="MXN"
            icon="payments"
            variant="error"
          />
          <SummaryBox
            label={isMultiMonth ? 'Rotación Promedio' : 'Rotación'}
            value={summary.inventoryTurnover.toFixed(2)}
            unit="veces"
            icon="autorenew"
            variant="primary"
            sub="benchmark 30-60/año"
          />
          <SummaryBox
            label="Shrinkage"
            value={formatPercent(summary.shrinkageRate)}
            unit=""
            icon="trending_down"
            variant={summary.shrinkageRate > 2 ? 'error' : 'tertiary'}
            sub="Target: <2%"
          />
        </section>
      )}

      {isMultiMonth && invAgg && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-6">
          <SummaryBox
            label="Stockouts"
            value={String(invAgg.inventory.stockoutIncidents)}
            unit="incidentes"
            icon="error"
            variant="error"
          />
          <SummaryBox
            label="Críticos"
            value={String(invAgg.inventory.criticalIncidents)}
            unit="días"
            icon="warning"
            variant="warning"
          />
          <SummaryBox
            label="Días Exceso"
            value={String(invAgg.inventory.excessDays)}
            unit="días"
            icon="trending_up"
            variant="primary"
          />
          <SummaryBox
            label="Shrinkage Total"
            value={String(invAgg.inventory.totalShrinkage)}
            unit="unidades"
            icon="trending_down"
            variant="error"
            sub={formatPercent(invAgg.inventory.shrinkageRate)}
          />
        </section>
      )}

      {isMultiMonth && invAgg && (
        <GlassCard variant="kinetic" padding="md" rounded="none" className="overflow-hidden mb-6">
          <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-outline">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // TOP_WASTED_SKU
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Top Productos con Merma
              </h3>
            </div>
          </div>
          {invAgg.inventory.topWastedSku ? (
            <div className="space-y-4">
              <div className="bg-surface border-2 border-error p-5 shadow-[3px_3px_0_0_#000000]">
                <p className="font-mono text-[10px] uppercase tracking-widest text-error mb-1">
                  ⚠ TOP DESPERDICIO
                </p>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-bold text-text-bright font-mono uppercase tracking-widest">
                      {invAgg.inventory.topWastedSku.code} · {invAgg.inventory.topWastedSku.name}
                    </p>
                    <p className="text-text-muted text-sm mt-1 font-mono">
                      // Principal candidato a reducir producción
                    </p>
                  </div>
                  <p className="font-condensed text-4xl text-error num">
                    {formatNumber(invAgg.inventory.topWastedSku.wasteUnits)} unid
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-text-muted font-mono uppercase tracking-widest text-sm py-12">
              // Sin merma significativa en los meses seleccionados
            </p>
          )}
        </GlassCard>
      )}

      {!isMultiMonth && (
        <GlassCard variant="kinetic" padding="md" rounded="none" className="overflow-hidden">
          <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-outline">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // REORDER_SUGGESTIONS
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Sugerencias de Compra
              </h3>
            </div>
            <button className="kinetic-button-outline">
              <span className="material-symbols-outlined text-base">download</span>
              Exportar PDF
            </button>
          </div>
          {suggestions.length === 0 ? (
            <p className="text-center text-text-muted font-mono uppercase tracking-widest text-sm py-12">
              // Sin sugerencias para este mes
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suggestions.map((s) => (
                <div
                  key={s.code}
                  className={cn(
                    'bg-surface border-2 border-outline p-4 cursor-pointer',
                    'shadow-[3px_3px_0_0_#000000]',
                    'transition-all duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[5px_5px_0_0_#ff8a65] hover:border-primary',
                  )}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-bold text-text-bright text-sm font-mono uppercase tracking-widest">
                        {s.code} · {s.name}
                      </p>
                      <SkuStatusBadge status={s.status} />
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                        Ordenar
                      </p>
                      <p className="font-condensed text-3xl text-primary num leading-none">
                        {s.suggestedOrder}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs border-t-2 border-outline pt-3">
                    <div>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                        Stock
                      </span>
                      <p className="font-bold num text-text-bright">{s.tei}</p>
                    </div>
                    <div>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                        DOH
                      </span>
                      <p className="font-bold num text-text-bright">{s.doh.toFixed(1)} d</p>
                    </div>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-3">
                    // {s.reason}
                  </p>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      )}

      {/* === STOCK DE SEGURIDAD + PUNTO DE REORDEN (PDF 5.4) === */}
      {!isMultiMonth && safetyStock.length > 0 && (
        <GlassCard variant="kinetic" padding="md" rounded="none" className="overflow-hidden mt-6">
          <div className="mb-4 pb-3 border-b-2 border-outline">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              // SAFETY_STOCK :: Z=1.96 (95% SERVICIO)
            </p>
            <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
              Stock de Seguridad + Punto de Reorden
            </h3>
            <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
              // SS = Z × σ × √(Lead Time) · ROP = Demanda × LT + SS
            </p>
          </div>
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-outline">
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                    SKU
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                    Demanda/día
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                    σ
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                    LT (d)
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-secondary text-right">
                    Stock Seg.
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-primary text-right">
                    ROP
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                    Stock Actual
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-center">
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody>
                {safetyStock.map((s) => (
                  <tr
                    key={s.code}
                    className={cn(
                      'border-b border-outline/50',
                      s.needsReorder && 'bg-error/10',
                    )}
                  >
                    <td className="px-3 py-2 font-mono text-xs text-text-bright">
                      <span className="inline-block px-1.5 py-0.5 bg-primary text-black border border-black font-bold mr-2">
                        {s.code}
                      </span>
                      {s.name}
                    </td>
                    <td className="px-3 py-2 num text-right text-text-bright">
                      {s.avgDailyDemand.toFixed(1)}
                    </td>
                    <td className="px-3 py-2 num text-right text-text-muted">
                      {s.demandStdDev.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 num text-right text-text-muted">
                      {s.leadTimeDays}
                    </td>
                    <td className="px-3 py-2 num text-right text-secondary font-bold">
                      {s.safetyStock}
                    </td>
                    <td className="px-3 py-2 num text-right text-primary font-bold">
                      {s.reorderPoint}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-2 num text-right font-bold',
                        s.needsReorder ? 'text-error' : 'text-text-bright',
                      )}
                    >
                      {s.currentStock}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {s.needsReorder ? (
                        <span className="kinetic-badge-danger">
                          <span className="material-symbols-outlined text-xs">priority_high</span>
                          Reordenar
                        </span>
                      ) : (
                        <span className="kinetic-badge-lime">
                          <span className="material-symbols-outlined text-xs">check</span>
                          OK
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}
    </>
  );
}

interface SummaryBoxProps {
  label: string;
  value: string;
  unit: string;
  icon: string;
  variant: 'primary' | 'tertiary' | 'warning' | 'error';
  sub?: string;
}

function SummaryBox({ label, value, unit, icon, variant, sub }: SummaryBoxProps) {
  const colorMap = {
    primary: 'border-primary text-primary',
    tertiary: 'border-tertiary text-tertiary',
    warning: 'border-warning text-warning',
    error: 'border-error text-error',
  };
  const iconBg = {
    primary: 'bg-primary',
    tertiary: 'bg-tertiary',
    warning: 'bg-warning',
    error: 'bg-error',
  };
  return (
    <div
      className={cn(
        'bg-surface border-2 p-5 shadow-[3px_3px_0_0_#000000] transition-transform duration-150 hover:-translate-x-[1px] hover:-translate-y-[1px] cursor-pointer',
        colorMap[variant],
      )}
    >
      <div
        className={cn(
          'w-10 h-10 flex items-center justify-center border-2 border-black text-black mb-3',
          iconBg[variant],
        )}
      >
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
        {label}
      </p>
      <p className="font-condensed text-3xl text-text-bright num leading-none">
        {value}
        {unit && (
          <span className="font-mono text-xs text-text-muted ml-2">{unit}</span>
        )}
      </p>
      {sub && (
        <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-2">
          {sub}
        </p>
      )}
    </div>
  );
}
